/**
 * The columns of the Payroll > Reports > Pay Report - one list.
 *
 * WHY THIS IS SHARED. The screen offers every column as a tick box, the CSV
 * writes the ticked ones, and the API draws the ticked ones on the PDF. Three
 * renderings of one choice, on two sides of the wire: a list each would agree
 * until somebody added a column to one of them.
 *
 * Kris, 2026-10-05, on what the report holds: *"should be able to include what
 * information you want in the report by clicking (employee/ company/ YTD /
 * period"*, each column ticked on its own, with select all and select none.
 * Those four words are the four groups below.
 *
 * NO LABELS HERE. The screen and the CSV read theirs from the payroll i18n
 * bundle (`payReport.columns.<key>`); the PDF is drawn by the API in English
 * like every other payroll document, from a map typed on `PayReportColumnKey`
 * so a column added here without a label there does not compile.
 *
 * THE EMPLOYEE'S NAME IS NOT A COLUMN. Every row is about somebody, and a
 * report with the name unticked is a table of figures belonging to no one.
 */

export const PAY_REPORT_GROUPS = ['employee', 'period', 'company', 'ytd'] as const;
export type PayReportGroup = (typeof PAY_REPORT_GROUPS)[number];

/**
 * How a value is written.
 *
 *   text, date   as they read
 *   money        pounds and pence; adds up into the subtotal and the total
 *   hours        a number of hours to two places; adds up the same way
 *   rate         pounds an hour. NOT added up - a column of rates has no sum
 */
export type PayReportColumnKind = 'text' | 'date' | 'money' | 'hours' | 'rate';

/**
 * Where a column's value lives.
 *
 *   employee  one value per person, the same on each of their lines
 *   line      one value per pay run line; money sums into the subtotal
 *   ytd       one value per person, shown on their subtotal row only - a
 *             year-to-date repeated down every line reads as a column to add up
 */
export type PayReportColumnScope = 'employee' | 'line' | 'ytd';

export interface PayReportColumnDef {
  key: string;
  group: PayReportGroup;
  kind: PayReportColumnKind;
  scope: PayReportColumnScope;
}

export const PAY_REPORT_COLUMNS = [
  // Employee - who the line is about, as the record stands today.
  { key: 'employeeNumber', group: 'employee', kind: 'text', scope: 'employee' },
  { key: 'department', group: 'employee', kind: 'text', scope: 'employee' },
  { key: 'site', group: 'employee', kind: 'text', scope: 'employee' },
  { key: 'jobTitle', group: 'employee', kind: 'text', scope: 'employee' },
  // The code the pay was taxed under, off the line - not today's record.
  { key: 'taxCode', group: 'employee', kind: 'text', scope: 'line' },
  { key: 'niCategory', group: 'employee', kind: 'text', scope: 'line' },

  // Period - the pay run, and what the employee was paid and had taken.
  { key: 'payDate', group: 'period', kind: 'date', scope: 'line' },
  { key: 'periodStart', group: 'period', kind: 'date', scope: 'line' },
  { key: 'periodEnd', group: 'period', kind: 'date', scope: 'line' },
  { key: 'payFrequency', group: 'period', kind: 'text', scope: 'line' },
  { key: 'runStatus', group: 'period', kind: 'text', scope: 'line' },
  // Off the run's rate rows - what Enter Payments recorded behind the gross.
  { key: 'payRate', group: 'period', kind: 'rate', scope: 'line' },
  { key: 'hours', group: 'period', kind: 'hours', scope: 'line' },
  { key: 'overtimeHours', group: 'period', kind: 'hours', scope: 'line' },
  { key: 'overtimePay', group: 'period', kind: 'money', scope: 'line' },
  { key: 'holidayHours', group: 'period', kind: 'hours', scope: 'line' },
  { key: 'holidayPay', group: 'period', kind: 'money', scope: 'line' },
  { key: 'grossPay', group: 'period', kind: 'money', scope: 'line' },
  { key: 'salarySacrifice', group: 'period', kind: 'money', scope: 'line' },
  { key: 'taxablePay', group: 'period', kind: 'money', scope: 'line' },
  { key: 'statutoryPay', group: 'period', kind: 'money', scope: 'line' },
  { key: 'otherPayments', group: 'period', kind: 'money', scope: 'line' },
  { key: 'paye', group: 'period', kind: 'money', scope: 'line' },
  { key: 'employeeNic', group: 'period', kind: 'money', scope: 'line' },
  // The qualifying earnings the two pension contributions were struck on.
  { key: 'pensionableEarnings', group: 'period', kind: 'money', scope: 'line' },
  { key: 'employeePension', group: 'period', kind: 'money', scope: 'line' },
  { key: 'studentLoan', group: 'period', kind: 'money', scope: 'line' },
  { key: 'postgradLoan', group: 'period', kind: 'money', scope: 'line' },
  { key: 'aeo', group: 'period', kind: 'money', scope: 'line' },
  { key: 'staffLoan', group: 'period', kind: 'money', scope: 'line' },
  { key: 'otherDeductions', group: 'period', kind: 'money', scope: 'line' },
  { key: 'netPay', group: 'period', kind: 'money', scope: 'line' },

  // Company - what the employer pays on top, for the same runs.
  { key: 'employerNic', group: 'company', kind: 'money', scope: 'line' },
  { key: 'employerPension', group: 'company', kind: 'money', scope: 'line' },

  // Year to date - the tax year the range ends in, up to the range's last day.
  { key: 'ytdTaxablePay', group: 'ytd', kind: 'money', scope: 'ytd' },
  { key: 'ytdPaye', group: 'ytd', kind: 'money', scope: 'ytd' },
  { key: 'ytdEmployeeNic', group: 'ytd', kind: 'money', scope: 'ytd' },
  { key: 'ytdEmployeePension', group: 'ytd', kind: 'money', scope: 'ytd' },
  { key: 'ytdStudentLoan', group: 'ytd', kind: 'money', scope: 'ytd' },
  { key: 'ytdPostgradLoan', group: 'ytd', kind: 'money', scope: 'ytd' },
  { key: 'ytdStatutory', group: 'ytd', kind: 'money', scope: 'ytd' },
  { key: 'ytdNetPay', group: 'ytd', kind: 'money', scope: 'ytd' },
  { key: 'ytdEmployerNic', group: 'ytd', kind: 'money', scope: 'ytd' },
  { key: 'ytdEmployerPension', group: 'ytd', kind: 'money', scope: 'ytd' },
] as const satisfies readonly PayReportColumnDef[];

export type PayReportColumnKey = (typeof PAY_REPORT_COLUMNS)[number]['key'];

export const PAY_REPORT_COLUMN_KEYS: readonly PayReportColumnKey[] = PAY_REPORT_COLUMNS.map(
  (c) => c.key,
);

type LineSumDef = Extract<
  (typeof PAY_REPORT_COLUMNS)[number],
  { scope: 'line'; kind: 'money' | 'hours' }
>;
/** The columns of a pay run line that add up - money and hours - in catalogue order. */
export type PayReportLineSumKey = LineSumDef['key'];
export const PAY_REPORT_LINE_SUM_KEYS: readonly PayReportLineSumKey[] = PAY_REPORT_COLUMNS.filter(
  (c): c is LineSumDef => c.scope === 'line' && (c.kind === 'money' || c.kind === 'hours'),
).map((c) => c.key);

type YtdDef = Extract<(typeof PAY_REPORT_COLUMNS)[number], { scope: 'ytd' }>;
/** The year-to-date columns, in catalogue order. */
export type PayReportYtdKey = YtdDef['key'];
export const PAY_REPORT_YTD_KEYS: readonly PayReportYtdKey[] = PAY_REPORT_COLUMNS.filter(
  (c): c is YtdDef => c.scope === 'ytd',
).map((c) => c.key);

export const PAY_REPORT_DATE_BASES = ['pay_date', 'period_end'] as const;
export type PayReportDateBasis = (typeof PAY_REPORT_DATE_BASES)[number];

/**
 * What a pay run on the report is, in the report's own words.
 *
 *   draft      composed and never finalised - only present when asked for
 *   finalised  locked: the figures are fixed
 *   submitted  locked and sent to HMRC
 *   reopened   finalised once, then reopened. Its money has gone out, so it
 *              counts as paid even though its status reads draft.
 */
export const PAY_REPORT_RUN_STATES = ['draft', 'finalised', 'submitted', 'reopened'] as const;
export type PayReportRunState = (typeof PAY_REPORT_RUN_STATES)[number];

/** The figures of a line that add up, and so also the shape of a subtotal and of the total. */
export type PayReportSums = Record<PayReportLineSumKey, number>;
export type PayReportYtd = Record<PayReportYtdKey, number>;

export interface PayReportLine extends PayReportSums {
  runId: string;
  payDate: string;
  periodStart: string;
  periodEnd: string;
  payFrequency: string;
  runStatus: PayReportRunState;
  taxCode: string;
  niCategory: string;
  /**
   * Pounds an hour on the run's Standard Rate row. Null where the run holds no
   * such row with hours on it - a salaried line, or one entered as a figure.
   */
  payRate: number | null;
}

export interface PayReportEmployee {
  employeeId: string;
  employeeName: string;
  employeeNumber: string | null;
  department: string | null;
  site: string | null;
  jobTitle: string | null;
  lines: PayReportLine[];
  subtotal: PayReportSums;
  /** Null when the person has no paid line in that tax year up to the date. */
  ytd: PayReportYtd | null;
}

export interface PayReportFilters {
  from: string;
  to: string;
  dateBasis: PayReportDateBasis;
  includeDrafts: boolean;
  employeeIds: string[];
  departmentIds: string[];
  siteIds: string[];
  payFrequencies: string[];
  /** Only lines with an employee or an employer pension contribution on them. */
  pensionOnly: boolean;
}

/**
 * The reports drawn from the pay run lines. Each is the same query and the
 * same rows; what differs is the title it prints under and, for the pension
 * report, a fixed set of columns.
 */
export const PAY_REPORT_KINDS = ['pay', 'pension'] as const;
export type PayReportKind = (typeof PAY_REPORT_KINDS)[number];

/**
 * Reports > Pension Contributions (TODO 1400). Kris, 2026-10-05, asked what a
 * row should show: the employee and employer amounts, and pensionable
 * earnings. The pay date is there because a contribution is nothing without
 * the run it belongs to. No scheme, no reason codes, no salary sacrifice -
 * offered and not chosen.
 */
export const PENSION_REPORT_COLUMNS = [
  'payDate',
  'pensionableEarnings',
  'employeePension',
  'employerPension',
] as const satisfies readonly PayReportColumnKey[];

export interface PayReport {
  filters: PayReportFilters;
  /** The tax year the year-to-date figures are for, and the day they run to. */
  ytdTaxYear: string;
  ytdAsAt: string;
  employees: PayReportEmployee[];
  total: PayReportSums;
  lineCount: number;
}

/** A row of the report as it is laid out: a pay run line, a person's subtotal, or the total. */
export type PayReportRow =
  | { kind: 'line'; employee: PayReportEmployee; line: PayReportLine }
  | { kind: 'subtotal'; employee: PayReportEmployee }
  | { kind: 'total'; report: PayReport };

/**
 * The value a column holds on a row, unformatted. Null is an empty cell.
 *
 * ONE RULE FOR WHICH CELLS ARE EMPTY, because the screen, the CSV and the PDF
 * each draw this table and three copies of "a subtotal has no pay date, a
 * total has no year to date" would drift:
 *
 *   line      everything but the year to date
 *   subtotal  the sums, and the person's year to date
 *   total     the sums only - a year to date is one person's, and a rate,
 *             a date or a tax code has no total
 */
export function payReportValue(
  def: (typeof PAY_REPORT_COLUMNS)[number],
  row: PayReportRow,
): string | number | null {
  const sums = def.scope === 'line' && (def.kind === 'money' || def.kind === 'hours');
  if (row.kind === 'total') {
    return sums ? row.report.total[def.key as PayReportLineSumKey] : null;
  }
  if (row.kind === 'subtotal') {
    if (def.scope === 'ytd') return row.employee.ytd?.[def.key] ?? null;
    return sums ? row.employee.subtotal[def.key as PayReportLineSumKey] : null;
  }
  if (def.scope === 'ytd') return null;
  if (def.scope === 'employee') return row.employee[def.key];
  return row.line[def.key];
}

/** The report's rows in the order they are read: each person's lines, their subtotal, then the total. */
export function payReportRows(report: PayReport): PayReportRow[] {
  const rows: PayReportRow[] = [];
  for (const employee of report.employees) {
    for (const line of employee.lines) rows.push({ kind: 'line', employee, line });
    rows.push({ kind: 'subtotal', employee });
  }
  if (report.employees.length > 0) rows.push({ kind: 'total', report });
  return rows;
}

/**
 * Whether any of these columns is a year-to-date one - which is when a
 * rendering owes the reader the line saying which tax year it is and the day
 * it runs to.
 */
export function payReportShowsYearToDate(
  defs: ReadonlyArray<(typeof PAY_REPORT_COLUMNS)[number]>,
): boolean {
  return defs.some((d) => d.scope === 'ytd');
}
